/**
 * @amplitude/element-selector
 *
 * Shared element-selector algorithm consumed by autocapture, the
 * app.amplitude.com tagging UI, and the Chrome extension visual tagger.
 *
 * This file is the package's public API surface. The orchestrator, fallback,
 * config resolver, and engine factory compose on top of the primitives
 * (types, pattern packs, helpers, strategies) to produce the runtime engine
 * consumers actually call.
 *
 * See the design doc:
 *   packages/plugin-autocapture-browser/element-selector-strategy-v1-no-classes.md
 */

export const PACKAGE_NAME = '@amplitude/element-selector';

// ===== Types =====
export type {
  Strategy,
  StrategyContext,
  ResolvedSelectorConfig,
  ElementSelectorRemoteConfig,
  ElementSelectorLogger,
  SelectorEngine,
} from './types';

// ===== Pattern packs =====
export {
  DEFAULT_AUTOGENERATED_ID_PATTERNS,
  compile as compileAutogeneratedIdPatterns,
  isStableId,
} from './patterns/autogenerated-ids';

export {
  DEFAULT_UNSTABLE_CLASS_PATTERNS,
  compile as compileUnstableClassPatterns,
  filterClasses,
} from './patterns/unstable-classes';

// ===== Helpers =====
export { getStableId } from './helpers/get-stable-id';
export { describeRelative } from './helpers/describe-relative';

// ===== Strategies =====
export { explicitTrackingAttribute } from './strategies/explicit-tracking-attribute';
export { stableId as stableIdStrategy } from './strategies/stable-id';

// ===== Orchestrator + fallback =====
// Exposed publicly so the dashboard / Chrome extension can call them ad-hoc
// without instantiating a full engine — useful for preview / debugging flows.
export { runOrchestrator, DEFAULT_STRATEGIES } from './orchestrator';
export type { OrchestratorOptions } from './orchestrator';
export { fallbackCssPath } from './fallback-css-path';

// ===== Legacy walker =====
// Canonical home for the Chromium-DevTools-derived `cssPath` algorithm.
// Both `plugin-autocapture-browser` and `session-replay-ui` historically
// shipped their own copies kept in sync by comment; both will switch to
// re-exporting from here so the two can never drift again. End consumers
// rarely need to call this directly — `engine.generate` and
// `generateSelector` route through it automatically — but it's exported so
// the SDK and dashboard can drop their local copies without breaking
// downstream imports.
export { legacyCssPath } from './legacy-css-path';

// ===== Config resolver =====
export { resolveSelectorConfig, DEFAULT_RESOLVED_CONFIG } from './config/resolve-config';

// ===== Engine factory =====
export { createSelectorEngine } from './engine';
export type { CreateSelectorEngineOptions } from './engine';

// ===== Top-level selector entry point =====
// Use this from React contexts / boot-time call sites where the engine may
// not yet be available. When the engine *is* present, just call
// `engine.generate(el)` directly — it has the same kill-switch semantics
// baked in.
export { generateSelector } from './generate-selector';
